import { useIntl } from "react-intl";

import { ProductCardBlockLogoLockups } from "@/libraries/design/components/product-card-block/src/product-card-block";
import { useLocalizedHref } from "@/libraries/web-framework";

export type softwareRecommendation = {
    productLogo: (typeof ProductCardBlockLogoLockups)[keyof typeof ProductCardBlockLogoLockups];
    productName: string;
    heroHeadline: string;
    heroSubheading: string;
    heroImage: string;
    heroImageMobile?: string;
    productCardDescription: string;
    productCardButtonLabel: string;
    productCardButtonUrl: string;
    productCardButtonType: "primary" | "secondary";
    productCardLinkText: string;
    productCardLinkPath: string;
};

export type softwareRecommendationProps = {
    jobFunction?: string | null;
    loggedInProducts: string[];
};

const GetProductList = (): Record<string, softwareRecommendation> => {
    const intl = useIntl();
    const tryNowLinkText = intl.formatMessage({
        id: "softwareRecommendation.tryNowLinkText",
        defaultMessage: "Try now"
    });
    const learnMoreLinkText = intl.formatMessage({
        id: "softwareRecommendation.learnMoreLinkText",
        defaultMessage: "Learn more"
    });
    return {
        jira: {
            productLogo: ProductCardBlockLogoLockups["jira"],
            productName: "Jira",
            heroHeadline: intl.formatMessage({
                id: "softwareRecommendation.jira.heroHeadline",
                defaultMessage: "Great outcomes start with Jira"
            }),
            heroSubheading: intl.formatMessage({
                id: "softwareRecommendation.jira.heroSubheading",
                defaultMessage:
                    "AI-powered project management that removes the work around work. Keep teams in sync and on track."
            }),
            heroImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/rj625af4d34vi1uwmqhg2w58r5j315cy.png",
            heroImageMobile:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/n1wrg60lrp5sh7pnsq083a36af5vfn4d.png",
            productCardDescription: intl.formatMessage({
                id: "softwareRecommendation.jira.productCardDescription",
                defaultMessage:
                    "Plan, track, and deliver your biggest ideas together with Jira - the project management tool for all teams."
            }),
            productCardButtonLabel: tryNowLinkText,
            productCardButtonUrl: useLocalizedHref(
                "/try/cloud/signup?bundle=jira-software&edition=free"
            ),
            productCardButtonType: "secondary",
            productCardLinkText: learnMoreLinkText,
            productCardLinkPath: useLocalizedHref("/software/jira")
        },
        confluence: {
            productLogo: ProductCardBlockLogoLockups["confluence"],
            productName: "Confluence",
            heroHeadline: intl.formatMessage({
                id: "softwareRecommendation.confluence.heroHeadline",
                defaultMessage: "Go from ideas to outcomes with Confluence"
            }),
            heroSubheading: intl.formatMessage({
                id: "softwareRecommendation.confluence.heroSubheading",
                defaultMessage:
                    "Create and organize work documents in one AI-powered workspace to unlock knowledge and collaborate with clarity."
            }),
            heroImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/056hog808o6op156yy7784t38pwxb0w5.png",
            heroImageMobile:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/n1wrg60lrp5sh7pnsq083a36af5vfn4d.png",
            productCardDescription: intl.formatMessage({
                id: "softwareRecommendation.confluence.productCardDescription",
                defaultMessage:
                    "Spend less time hunting things down and more time getting things done. Organize your work, create documents, and discuss everything in one place."
            }),
            productCardButtonLabel: tryNowLinkText,
            productCardButtonUrl: useLocalizedHref(
                "/try/cloud/signup?bundle=confluence&edition=free"
            ),
            productCardButtonType: "secondary",
            productCardLinkText: learnMoreLinkText,
            productCardLinkPath: useLocalizedHref("/software/confluence")
        },
        jsm: {
            productLogo: ProductCardBlockLogoLockups["jiraServiceManagement"],
            productName: "Jira Service Management",
            heroHeadline: intl.formatMessage({
                id: "softwareRecommendation.jsm.heroHeadline",
                defaultMessage: "Unlock high-velocity service management"
            }),
            heroSubheading: intl.formatMessage({
                id: "softwareRecommendation.jsm.heroSubheading",
                defaultMessage:
                    "Unite teams on a single AI-powered platform to deliver exceptional service at scale."
            }),
            heroImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pj46a2usrxr58068ate7gwf8870lk5n8.png",
            productCardDescription: intl.formatMessage({
                id: "softwareRecommendation.jsm.productCardDescription",
                defaultMessage:
                    "Unite teams on a single AI-powered platform to deliver service at scale."
            }),
            productCardButtonLabel: tryNowLinkText,
            productCardButtonUrl: useLocalizedHref(
                "/try/cloud/signup?bundle=jira-service-management"
            ),
            productCardButtonType: "secondary",
            productCardLinkText: learnMoreLinkText,
            productCardLinkPath: useLocalizedHref(
                "/software/jira/service-management"
            )
        },
        loom: {
            productLogo: ProductCardBlockLogoLockups["loom"],
            productName: "Loom",
            heroHeadline: intl.formatMessage({
                id: "softwareRecommendation.loom.heroHeadline",
                defaultMessage: "Move work forward with AI-powered video"
            }),
            heroSubheading: intl.formatMessage({
                id: "softwareRecommendation.loom.heroSubheading",
                defaultMessage:
                    "Record your screen, your camera, and your voice to share updates or feedback with your team."
            }),
            heroImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pj46a2usrxr58068ate7gwf8870lk5n8.png",
            productCardDescription: intl.formatMessage({
                id: "softwareRecommendation.loom.productCardDescription",
                defaultMessage:
                    "Move work forward by sharing Loom video messages and AI-powered meeting recaps."
            }),
            productCardButtonLabel: tryNowLinkText,
            productCardButtonUrl: useLocalizedHref(
                "/try/cloud/signup?bundle=loom&edition=premium"
            ),
            productCardButtonType: "secondary",
            productCardLinkText: learnMoreLinkText,
            productCardLinkPath: useLocalizedHref("/software/loom")
        },
        jpd: {
            productLogo: ProductCardBlockLogoLockups["jiraProductDiscovery"],
            productName: "Jira Product Discovery",
            heroHeadline: intl.formatMessage({
                id: "softwareRecommendation.jpd.heroHeadline",
                defaultMessage: "Build the right thing"
            }),
            heroSubheading: intl.formatMessage({
                id: "softwareRecommendation.jpd.heroSubheading",
                defaultMessage:
                    "Prioritize your roadmap by impact, effort, goals and more. Connect to delivery work in Jira."
            }),
            heroImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pj46a2usrxr58068ate7gwf8870lk5n8.png",
            productCardDescription: intl.formatMessage({
                id: "softwareRecommendation.jpd.productCardDescription",
                defaultMessage:
                    "Build the right thing with Jira Product Discovery. Capture feedback, prioritize ideas, and create roadmaps tied to delivery in Jira."
            }),
            productCardButtonLabel: tryNowLinkText,
            productCardButtonUrl: useLocalizedHref(
                "/try/cloud/signup?bundle=jira-product-discovery"
            ),
            productCardButtonType: "secondary",
            productCardLinkText: learnMoreLinkText,
            productCardLinkPath: useLocalizedHref(
                "/software/jira/product-discovery"
            )
        },
        rovo: {
            productLogo: ProductCardBlockLogoLockups["rovo"],
            productName: "Rovo",
            heroHeadline: intl.formatMessage({
                id: "softwareRecommendation.rovo.heroHeadline",
                defaultMessage: "Unleash AI-powered teamwork with Rovo"
            }),
            heroSubheading: intl.formatMessage({
                id: "softwareRecommendation.rovo.heroSubheading",
                defaultMessage:
                    "Connect your company’s knowledge, people, and workflows so teams can work smarter, faster - and together."
            }),
            heroImage:
                "https://dam-cdn.atl.orangelogic.com/AssetLink/pj46a2usrxr58068ate7gwf8870lk5n8.png",
            productCardDescription: intl.formatMessage({
                id: "softwareRecommendation.rovo.productCardDescription",
                defaultMessage:
                    "Help every team work smarter and faster with AI-powered Search, Chat and Agents - or build custom agents, automations, and apps with Studio."
            }),
            productCardButtonLabel: tryNowLinkText,
            productCardButtonUrl: useLocalizedHref(
                "/try/cloud/signup?bundle=teamwork-collection&primaryCollectionProduct=rovo.ondemand&edition=premium"
            ),
            productCardButtonType: "secondary",
            productCardLinkText: learnMoreLinkText,
            productCardLinkPath: useLocalizedHref("/software/rovo")
        }
    };
};

const categoryOneJobFunctions = [
    "engineering",
    "information technology",
    "customer service",
    "human resources",
    "data science",
    "operations",
    "finance",
    "legal"
];

const categoryTwoJobFunctions = ["product management"];

const categoryThreeJobFunctions = [
    "program management",
    "c-suite",
    "sales",
    "marketing",
    "design",
    "other"
];

// Helper functions for product recommendation logic ========================================================

/**
 * Determines the product ranking order based on job function category
 */
const getRankedProducts = (
    PRODUCT_LIST: Record<string, softwareRecommendation>,
    jobFunction: string | null
): string[] => {
    if (!jobFunction) {
        // Default: use PRODUCT_LIST order when jobFunction is null
        return Object.keys(PRODUCT_LIST);
    }

    if (categoryOneJobFunctions.includes(jobFunction)) {
        // Category One: jira, confluence, jsm, loom, jpd, rovo
        return ["jira", "confluence", "jsm", "loom", "jpd", "rovo"];
    }

    if (categoryTwoJobFunctions.includes(jobFunction)) {
        // Category Two: jira, confluence, jpd, loom, rovo, jsm
        return ["jira", "confluence", "jpd", "loom", "rovo", "jsm"];
    }

    if (categoryThreeJobFunctions.includes(jobFunction)) {
        // Category Three: jira, confluence, loom, rovo, jpd, jsm
        return ["jira", "confluence", "loom", "rovo", "jpd", "jsm"];
    }

    // Fallback: use PRODUCT_LIST order for unknown job functions
    return Object.keys(PRODUCT_LIST);
};

/**
 * Applies special handling for Category One: when 1 product is owned,
 * prioritizes rovo over jpd by swapping their positions
 */
const applyCategoryOneSwap = (
    filteredProductData: string[],
    jobFunction: string | null,
    normalizedLoggedInProducts: string[]
): void => {
    const shouldApplySwap =
        categoryOneJobFunctions.includes(jobFunction || "") &&
        normalizedLoggedInProducts.length === 1 &&
        filteredProductData.length >= 4;

    if (!shouldApplySwap) {
        return;
    }

    const jpdIndex = filteredProductData.indexOf("jpd");
    const rovoIndex = filteredProductData.indexOf("rovo");

    if (jpdIndex !== -1 && rovoIndex !== -1 && jpdIndex < rovoIndex) {
        // Swap jpd and rovo positions so rovo comes before jpd
        const jpdValue = filteredProductData[jpdIndex];
        const rovoValue = filteredProductData[rovoIndex];

        if (jpdValue && rovoValue) {
            filteredProductData[jpdIndex] = rovoValue;
            filteredProductData[rovoIndex] = jpdValue;
        }
    }
};

/**
 * Determines the fill order when adding owned products back
 * For Category Two and Three, prioritizes jsm and loom
 */
const getFillOrder = (
    jobFunction: string | null,
    rankedProducts: string[]
): string[] => {
    const shouldUsePrioritizedFill =
        categoryTwoJobFunctions.includes(jobFunction || "") ||
        categoryThreeJobFunctions.includes(jobFunction || "");

    if (shouldUsePrioritizedFill) {
        // Prioritize jsm and loom when filling for Category Two and Three
        return ["jira", "confluence", "jsm", "loom", "jpd", "rovo"];
    }

    return rankedProducts;
};

/**
 * Fills the filtered product data with owned products if needed
 * to reach the target of 4 products
 */
const fillWithOwnedProducts = (
    filteredProductData: string[],
    normalizedLoggedInProducts: string[],
    fillOrder: string[]
): void => {
    while (filteredProductData.length < 4) {
        const currProduct = fillOrder.find(
            (product) =>
                normalizedLoggedInProducts.includes(product.toLowerCase()) &&
                !filteredProductData.includes(product)
        );

        if (currProduct) {
            filteredProductData.push(currProduct);
        } else {
            // If no more unique products available, break to avoid infinite loop
            break;
        }
    }
};

/**
 * Main function to get recommended products based on job function and owned products
 * Returns up to 4 products, prioritizing products the user doesn't own
 */
export const getFilteredProductData = (
    PRODUCT_LIST: Record<string, softwareRecommendation>,
    jobFunction: string | null,
    loggedInProducts: string[]
): string[] => {
    const normalizedLoggedInProducts = loggedInProducts.map((product) =>
        product.toLowerCase()
    );

    const rankedProducts = getRankedProducts(PRODUCT_LIST, jobFunction);

    // Get products NOT in listed products for logged in user
    const filteredProductData = rankedProducts.filter(
        (product) => !normalizedLoggedInProducts.includes(product.toLowerCase())
    );

    // Apply Category One special swap if applicable
    applyCategoryOneSwap(
        filteredProductData,
        jobFunction,
        normalizedLoggedInProducts
    );

    // If we have 4 or more products not owned, return first 4
    if (filteredProductData.length >= 4) {
        return filteredProductData.slice(0, 4);
    }

    // Fill with owned products if needed
    const fillOrder = getFillOrder(jobFunction, rankedProducts);
    fillWithOwnedProducts(
        filteredProductData,
        normalizedLoggedInProducts,
        fillOrder
    );

    return filteredProductData;
};

// End helper function to get recommended products ========================================================

/**
 * Custom code to get recommended products based on job function and owned products
 * Returns an array of up to 4 recommended products
 */
export const SoftwarePageProductRecommendation = ({
    jobFunction = null,
    loggedInProducts
}: softwareRecommendationProps): softwareRecommendation[] => {
    const PRODUCT_LIST = GetProductList();
    // Convert empty string to null to match getFilteredProductData's expected type
    const normalizedJobFunction = jobFunction || null;
    const recommendedProducts = getFilteredProductData(
        PRODUCT_LIST,
        normalizedJobFunction,
        loggedInProducts
    );

    // Map product keys to product data, filtering out any undefined values
    // This ensures we never return undefined products if a key doesn't exist in PRODUCT_LIST
    const recommendedProductsData = recommendedProducts
        .map((product) => PRODUCT_LIST[product])
        .filter(
            (product): product is softwareRecommendation =>
                product !== undefined
        );

    return recommendedProductsData;
};

export default SoftwarePageProductRecommendation;
